import React, { useState } from "react";

type SourceType = string | React.FC<React.SVGAttributes<SVGElement>>;

interface ImageProps {
  source: SourceType;
  altText?: string;
  className?: string;
  onError?: () => void;
}

export const Image: React.FC<ImageProps> = ({
  source,
  altText = "",
  className,
  onError,
}) => {
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    // If onError is provided, execute it and prevent hasError update
    if (onError) {
      return onError();
    }
    setHasError(true);
  };

  // If source is missing or error happened, show fallback
  if (!source || hasError) {
    return (
      <div
        className={className}
        role="img"
        aria-label={altText || "Image failed to load"}
      >
        {/* fallback placeholder */}
        {altText || "No image"}
      </div>
    );
  }

  // Check if source is a React component (SVG)
  if (typeof source === "function") {
    const SvgComponent = source;
    return (
      <SvgComponent className={className} aria-label={altText} role="img" />
    );
  }

  // Otherwise, source is a string URL (PNG, JPG, or SVG URL)
  return (
    <img
      src={source}
      alt={altText}
      className={className}
      loading="lazy"
      onError={handleError}
      aria-hidden={!altText}
      style={{ display: "inline-block" }}
    />
  );
};
